import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Gift, Copy, Check, Share2, Users } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

/**
 * ReferralCard – share & earn block for the client Dashboard Overview.
 * The referral code defaults to the user's clientId so it's stable and short.
 */
const ReferralCard = ({ user }) => {
  const [copied, setCopied] = useState(false);

  const referralCode = useMemo(() => {
    if (!user) return 'DROP';
    if (user.referralCode) return String(user.referralCode).toUpperCase();
    if (user.clientId) return `DROP${user.clientId}`;
    return `DROP${String(user.principalId || '').slice(-4).toUpperCase()}`;
  }, [user]);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/?ref=${referralCode}`;
  }, [referralCode]);

  const shareText = `🚀 Start your dropshipping business with DropSquadz! Use my referral code ${referralCode} to get an exclusive discount. ${shareUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: 'Link copied!', description: 'Share it on WhatsApp, Instagram, or anywhere.' });
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast({ title: 'Copy failed', description: 'Please copy manually.', variant: 'destructive' });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'DropSquadz Referral', text: shareText, url: shareUrl });
      } catch (e) {
        // user cancelled
      }
    } else {
      // Fallback: WhatsApp share intent
      window.open(`https://api.whatsapp.com/send/?text=${encodeURIComponent(shareText)}`, '_blank');
    }
  };

  return (
    <Card
      className="border-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 text-white shadow-xl overflow-hidden relative"
      data-testid="referral-card"
    >
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full" />
      <div className="absolute -bottom-10 -left-6 w-40 h-40 bg-white/5 rounded-full" />

      <CardContent className="p-4 sm:p-6 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-base sm:text-lg font-bold leading-tight">Refer & Earn</h3>
                <span className="text-[10px] sm:text-xs px-2 py-0.5 bg-yellow-400 text-yellow-900 font-bold rounded-full">NEW</span>
              </div>
              <p className="text-blue-100 text-xs sm:text-sm leading-snug">
                Invite friends to DropSquadz. Get ₹200 credit when they activate a plan.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-xs sm:text-sm text-blue-100 sm:ml-2 flex-shrink-0">
            <Users className="w-4 h-4" />
            <span className="font-semibold text-white">{user?.referralsCount || 0}</span>
            <span>referrals</span>
          </div>
        </div>

        {/* Code chip + actions */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-blue-100 uppercase tracking-wider">Your Code</p>
              <p className="font-mono font-extrabold text-lg sm:text-xl truncate" data-testid="referral-code">
                {referralCode}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              data-testid="copy-referral-link-btn"
              aria-label="Copy referral link"
              className="w-10 h-10 rounded-lg bg-white text-blue-700 flex items-center justify-center active:scale-95 transition-all flex-shrink-0"
            >
              {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>

          <Button
            onClick={handleShare}
            data-testid="share-referral-btn"
            className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-5 py-3 sm:py-2 h-auto sm:h-12 shadow-lg w-full sm:w-auto"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralCard;
